import SelectBox from "../common/SelectBox";
import styles from "./Marker.module.css";
import { ReactComponent as MarkerSelect1 } from "../../img/icon/mk1_select.svg";
import { ReactComponent as MarkerSelect2 } from "../../img/icon/mk2_select.svg";
import { ReactComponent as MarkerSelect3 } from "../../img/icon/mk3_select.svg";
import { Dispatch, SetStateAction, SyntheticEvent, useRef, useEffect, useCallback, WheelEvent } from "react";
import Button from "../common/Button";
import { markerInfoType } from "./CreateMap";
import Icon from "../common/Icon";
import Tooltip, { TriggerType } from "../common/Tooltip";
import { useModal } from "../modal/Modal";
import ModalButtons from "../modal/ModalButtons";

interface MarkerProps {
    canProceed: boolean;
    sampleData: {
        row1: string[];
        row2: string[];
    };
    optionByCategory: {
        optionByCategory: { [fieldName: string]: string[] };
    };
    markerInfo: markerInfoType;
    markerSettingFn: {
        setMarkerInfo: Dispatch<SetStateAction<markerInfoType>>;
        selectMarkerLabel: (option: number) => void;
    };
}

interface MarkerItem {
    markerColor: string;
    marker: string;
}

export const colorItem: string[] = [
    "#ba2022",
    "#d54914",
    "#ffd500",
    "#017439",
    "#0066a1",
    "#352a54",
    "#653179",
    "#851a4e",
    "#f9107b",
    "#ff7b58",
    "#cc9933",
    "#8ab532",
    "#109496",
    "#687cae",
    "#9865ce",
    "#cc6092",
];

/**
 * @author cjs
 * @date 2024-09-12 11:21:47
 * @remark 지도만들기 마커 설정 사용자 설정 컴포넌트
 * @pram props
 * @props MarkerProps
 *      sampleData(required) : 마커 설정과 장소 정보 설정에 미리 보여주기 위한 예제 데이터
        optionByCategory(required) : 마커 설정 화면에 카테고리별설정 selectBox 데이터 
        markerInfo(required) : 마커 설정한 데이터
        markerSettingFn(required) : 마커 설정 데이터 변경 함수
    @returns Marker
 */

function Marker(props: MarkerProps) {
    const scrollRef = useRef<HTMLUListElement>(null);
    const showModalOnceRef = useRef(false);
    const { ModalComponent, openModal, closeModal } = useModal();

    // helper: 카테고리 없음(0) 일 때 markerInfo 를 단일 항목으로 보장
    const ensureSingleMarkerInfo = (mi: markerInfoType) => {
        const copy = { ...mi };
        if (!copy.markerInfo || copy.markerInfo.length === 0) {
            copy.markerInfo = [{ marker: "markerSelect1", markerColor: colorItem[0] }];
            copy.selectColorIdx = 0;
            copy.groupIdx = 0;
        } else if (copy.markerInfo.length > 1) {
            // 선택안함 모드에서는 단일화: selectColorIdx 가 유효하면 그 항목을 남김
            const keepIdx = Math.max(0, Math.min(copy.selectColorIdx ?? 0, copy.markerInfo.length - 1));
            const keep = copy.markerInfo[keepIdx] ?? copy.markerInfo[0];
            copy.markerInfo = [{ ...keep }];
            copy.selectColorIdx = 0;
            copy.groupIdx = 0;
        }
        return copy;
    };

    const checkGroupLimitExceeded = useCallback(
        (fieldName?: string, isFromSelect?: boolean) => {
            const _fieldName = fieldName ?? Object.keys(props.optionByCategory.optionByCategory)[props.markerInfo.categoryOptionIdx];
            const selectedGroupValues = props.optionByCategory.optionByCategory[_fieldName] || [];
            const isExceeded = selectedGroupValues.length > 16;

            if (isExceeded) {
                if (!showModalOnceRef.current || !isFromSelect) {
                    openModal({
                        title: "안내",
                        children: (
                            <>
                                <p>
                                    <b>'{_fieldName}'</b> 필드는 그룹 개수가 {selectedGroupValues.length}개로, 표시 가능한 최대 카테고리 수(16개)를
                                    초과합니다.
                                </p>
                                <p>다른 필드를 선택해주세요.</p>
                            </>
                        ),
                        modalButtons: (
                            <ModalButtons
                                buttonCount={1}
                                buttons={[
                                    {
                                        label: "확인",
                                        color: "Primary",
                                        onClick: () => closeModal(),
                                    },
                                ]}
                            />
                        ),
                    });
                    showModalOnceRef.current = true;
                }
            } else {
                showModalOnceRef.current = false;
            }

            return isExceeded;
        },
        [props.optionByCategory, props.markerInfo.categoryOptionIdx]
    );

    useEffect(() => {
        checkGroupLimitExceeded();
    }, [checkGroupLimitExceeded]);

    const wheelEvent = (event: WheelEvent<HTMLUListElement>) => {
        event.stopPropagation();
        if (!scrollRef.current) return;
        scrollRef.current.scrollLeft += event.deltaY;
    };

    // 기존: 그룹별 마커 일괄 변경
    const clickMarker = (e: SyntheticEvent) => {
        const id = (e.currentTarget as HTMLElement).id;
        const _copyMarkerInfo = { ...props.markerInfo };
        _copyMarkerInfo.markerInfo = _copyMarkerInfo.markerInfo.map((value) => {
            return { ...value, marker: id };
        });
        props.markerSettingFn.setMarkerInfo(_copyMarkerInfo);
    };

    // 그룹별 색 변경
    const clickColor = (data: string, i: number) => {
        const _copyMarkerInfo = { ...props.markerInfo };
        _copyMarkerInfo.markerInfo[_copyMarkerInfo.groupIdx].markerColor = data;
        _copyMarkerInfo.selectColorIdx = i;
        props.markerSettingFn.setMarkerInfo(_copyMarkerInfo);
    };

    // 단일 마커/색 변경 (선택안함 모드)
    const clickMarkerForNone = (e: SyntheticEvent) => {
        const id = (e.currentTarget as HTMLElement).id;
        let _copy = ensureSingleMarkerInfo({ ...props.markerInfo });
        _copy.markerInfo[0] = { ..._copy.markerInfo[0], marker: id };
        props.markerSettingFn.setMarkerInfo(_copy);
    };

    const clickColorForNone = (data: string, i: number) => {
        let _copy = ensureSingleMarkerInfo({ ...props.markerInfo });
        _copy.markerInfo[0].markerColor = data;
        _copy.selectColorIdx = i;
        props.markerSettingFn.setMarkerInfo(_copy);
    };

    const autoSetting = () => {
        // selectBox의 0번이 선택안함이면 markerInfo 단일 자동설정
        if (props.markerInfo.categoryOptionIdx === 0) {
            let _copy = ensureSingleMarkerInfo({ ...props.markerInfo });
            _copy.markerInfo[0] = {
                marker: randomMarker(),
                markerColor: randomColor(),
            };
            _copy.selectColorIdx = 0;
            props.markerSettingFn.setMarkerInfo(_copy);
            return;
        }

        const _copyMarkerInfo = { ...props.markerInfo };
        const markerItems: MarkerItem[] = [];
        const fieldName = Object.keys(props.optionByCategory.optionByCategory)[props.markerInfo.categoryOptionIdx] || "";
        let initMarker = randomMarker();
        (props.optionByCategory.optionByCategory[fieldName] || []).forEach(() => {
            markerItems.push({
                marker: initMarker,
                markerColor: randomColor(),
            });
        });
        _copyMarkerInfo.markerInfo = markerItems;
        props.markerSettingFn.setMarkerInfo(_copyMarkerInfo);
    };

    const selectGroupOption = (n: number) => {
        const _copyMarkerInfo = { ...props.markerInfo };
        _copyMarkerInfo.groupIdx = n;
        props.markerSettingFn.setMarkerInfo(_copyMarkerInfo);
    };

    const markerItem: string[] = ["markerSelect1", "markerSelect2", "markerSelect3"];
    const randomMarker = () => markerItem[Math.floor(Math.random() * markerItem.length)];
    const randomColor = () => colorItem[Math.floor(Math.random() * colorItem.length)];

    // SelectBox 선택 시 동작: option === 0 => 선택안함 모드 처리
    const selectCategoryOption = (option: number) => {
        let _copyMarkerInfo = { ...props.markerInfo };
        _copyMarkerInfo.categoryOptionIdx = option;
        _copyMarkerInfo.groupIdx = 0;

        if (option === 0) {
            // 선택안함: markerInfo 단일화 (현재 selectColorIdx 기준 보존)
            _copyMarkerInfo = ensureSingleMarkerInfo(_copyMarkerInfo);
        } else {
            // 카테고리 선택: 해당 그룹 수에 맞춰 markerInfo 생성
            const fieldName = Object.keys(props.optionByCategory.optionByCategory)[option];
            const values = props.optionByCategory.optionByCategory[fieldName] || [];
            _copyMarkerInfo.markerInfo = values.map(() => ({
                marker: randomMarker(),
                markerColor: randomColor(),
            }));
            // selectColorIdx 는 그룹별 컨텍스트가 아니므로 0으로 초기화
            _copyMarkerInfo.selectColorIdx = 0;
        }

        props.markerSettingFn.setMarkerInfo(_copyMarkerInfo);

        // 카테고리 변경 시 그룹 개수 초과 검사
        const fieldNameForCheck = option === 0 ? undefined : Object.keys(props.optionByCategory.optionByCategory)[option];
        checkGroupLimitExceeded(fieldNameForCheck, true);
    };

    return (
        <>
            <ModalComponent />
            <div className={styles.wrapSelectBox}>
                <div className={styles.selectBox}>
                    <div className={styles.selectBoxNameWrapper}>
                        <h2 className={styles.selectBoxName}>카테고리</h2>
                        <Tooltip
                            message={
                                <>
                                    <p>
                                        카테고리는 지도에서 최대 <b>16개까지</b> 그룹화해 표시됩니다.
                                    </p>
                                    <p>
                                        <b>선택한 필드의 그룹 개수가 16개를 초과</b>하면 다음 단계로 진행할 수 없습니다.
                                    </p>
                                </>
                            }
                            triggerType={TriggerType.HOVER}
                        >
                            <Icon name={"help"} color={"#0088FF"} style={{ marginLeft: 10, marginTop: 5, cursor: "help" }} />
                        </Tooltip>
                    </div>
                    {/* SelectBox의 0번째 항목을 '선택안함'으로 사용 */}
                    <SelectBox
                        optionList={Object.keys(props.optionByCategory.optionByCategory)}
                        selectOption={selectCategoryOption}
                        selected={props.markerInfo.categoryOptionIdx}
                    />
                </div>

                <div className={styles.selectBox}>
                    <div className={styles.selectBoxNameWrapper}>
                        <h2 className={styles.selectBoxName}>장소이름</h2>
                        <Tooltip
                            message={
                                <>
                                    <p>'장소이름'은 '카테고리'와 동일한 필드를 설정할 수 없습니다.</p>
                                </>
                            }
                            triggerType={TriggerType.HOVER}
                        >
                            <Icon name={"help"} color={"#0088FF"} style={{ marginLeft: 10, marginTop: 5, cursor: "help" }} />
                        </Tooltip>
                    </div>
                    <SelectBox
                        optionList={props.sampleData.row1}
                        selected={props.markerInfo.fieldNameIdx}
                        selectOption={props.markerSettingFn.selectMarkerLabel}
                    />
                </div>
            </div>

            <div>
                <div className={styles.categorySettingHeader}>
                    <h2 className={styles.selectBoxName}>카테고리별설정</h2>
                    <Button
                        label={"자동설정"}
                        size={"small"}
                        background={"#47a930"}
                        color={"white"}
                        margin={"0px 0px 5px 0px"}
                        onClick={autoSetting}
                    />
                </div>

                {/* 분기: SelectBox의 선택 인덱스가 0이면 '선택안함' 모드 (단일 마커/색) */}
                {props.markerInfo.categoryOptionIdx === 0 ? (
                    <>
                        <p style={{ margin: "4px 0 8px 0", color: "#666" }}>
                            카테고리를 사용하지 않습니다. 모든 POI는 동일한 마커/색으로 표시됩니다.
                        </p>
                        <div>
                            <h2 className={styles.selectBoxName}>단일 마커 모양 및 색상</h2>
                            <div className={styles.wrapMarker}>
                                <div className={styles.wrapMarkerList}>
                                    <ul className={styles.wrapMarkerItem} onWheel={wheelEvent} ref={scrollRef}>
                                        <li>
                                            <MarkerSelect1
                                                id="markerSelect1"
                                                className={`${styles.markerItem} ${
                                                    (props.markerInfo.markerInfo[0]?.marker || "markerSelect1") === "markerSelect1"
                                                        ? styles.select
                                                        : ""
                                                }`}
                                                onClick={clickMarkerForNone}
                                            />
                                        </li>
                                        <li>
                                            <MarkerSelect2
                                                id="markerSelect2"
                                                className={`${styles.markerItem} ${
                                                    (props.markerInfo.markerInfo[0]?.marker || "markerSelect1") === "markerSelect2"
                                                        ? styles.select
                                                        : ""
                                                }`}
                                                onClick={clickMarkerForNone}
                                            />
                                        </li>
                                        <li>
                                            <MarkerSelect3
                                                id="markerSelect3"
                                                className={`${styles.markerItem} ${
                                                    (props.markerInfo.markerInfo[0]?.marker || "markerSelect1") === "markerSelect3"
                                                        ? styles.select
                                                        : ""
                                                }`}
                                                onClick={clickMarkerForNone}
                                            />
                                        </li>
                                    </ul>
                                </div>
                                <div className={styles.wrapColorItem}>
                                    {colorItem.map((data, idx) => {
                                        const curColor = props.markerInfo.markerInfo[0]?.markerColor || colorItem[0];
                                        return (
                                            <button
                                                key={idx}
                                                className={`${styles.colorItem} ${data === curColor ? styles.selectColor : ""}`}
                                                style={{ backgroundColor: data }}
                                                onClick={() => clickColorForNone(data, idx)}
                                            ></button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <SelectBox
                            optionList={
                                props.optionByCategory.optionByCategory[
                                    Object.keys(props.optionByCategory.optionByCategory)[props.markerInfo.categoryOptionIdx]
                                ] || []
                            }
                            selected={props.markerInfo.groupIdx}
                            selectOption={selectGroupOption}
                        />

                        <div>
                            <h2 className={styles.selectBoxName}>마커모양 및 색상</h2>
                            <div className={styles.wrapMarker}>
                                <div className={styles.wrapMarkerList}>
                                    <ul className={styles.wrapMarkerItem} onWheel={wheelEvent} ref={scrollRef}>
                                        <li>
                                            <MarkerSelect1
                                                id="markerSelect1"
                                                className={`${styles.markerItem} ${
                                                    props.markerInfo.markerInfo[props.markerInfo.groupIdx]?.marker === "markerSelect1"
                                                        ? styles.select
                                                        : ""
                                                }`}
                                                onClick={clickMarker}
                                            />
                                        </li>
                                        <li>
                                            <MarkerSelect2
                                                id="markerSelect2"
                                                className={`${styles.markerItem} ${
                                                    props.markerInfo.markerInfo[props.markerInfo.groupIdx]?.marker === "markerSelect2"
                                                        ? styles.select
                                                        : ""
                                                }`}
                                                onClick={clickMarker}
                                            />
                                        </li>
                                        <li>
                                            <MarkerSelect3
                                                id="markerSelect3"
                                                className={`${styles.markerItem} ${
                                                    props.markerInfo.markerInfo[props.markerInfo.groupIdx]?.marker === "markerSelect3"
                                                        ? styles.select
                                                        : ""
                                                }`}
                                                onClick={clickMarker}
                                            />
                                        </li>
                                    </ul>
                                </div>
                                <div className={styles.wrapColorItem}>
                                    {colorItem.map((data, idx) => {
                                        const curColor = props.markerInfo.markerInfo[props.markerInfo.groupIdx]?.markerColor || colorItem[0];
                                        return (
                                            <button
                                                key={idx}
                                                className={`${styles.colorItem} ${data === curColor ? styles.selectColor : ""}`}
                                                style={{ backgroundColor: data }}
                                                onClick={() => clickColor(data, idx)}
                                            ></button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </>
    );
}

export default Marker;
